'use strict';

import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import moment from 'moment';
import redirectURL from '../redirectURL';
import { getHyphenDDMMMYYYYHHMM, getHyphenDDMMYYYY } from '../common/utils';
import {
  COLORS,
  MULTI_SELECT_STYLES,
  safeArray,
  groupBy,
  distinctValues,
  round1,
  isDeliveredRow,
  baseChartOptions,
  baseAxisLine,
  baseValueAxis,
  GridCard,
} from './dashboardShared';
import './performanceDashboardModern.css';

/*
 * Proof of Delivery (POD) Dashboard - the 5th top-level dashboard. Maps
 * directly onto the PDF's "EPOD Report" section (page 3): Total delivered
 * KPI, Total received/pending POD's, Transporter wise Received vs Pending,
 * Status Comparison by Aging Bucket, Monthly Received vs Pending, plus a
 * Shipment Level Dump. Sourced from the same consignments/getDataForPowerBiDashBoard
 * feed as Performance/Transporter/Inbound.
 *
 * pod_status on this feed is "Submitted" or blank - there is no literal
 * "Pending" value stored anywhere (confirmed from real sample responses).
 * podStatusOf below derives it: blank pod_status on a delivered row =
 * Pending, matching how the Performance Dashboard's own Shipment Dump
 * already treats this field.
 *
 * "POD Delay Days" (used by the PDF's aging-bucket SWITCH) was never a
 * confirmed stored field either. podDelayDaysOf derives it from two
 * confirmed real fields - delivery_datetime and pod_upload_date - using
 * the exact same INT(TODAY()-X) "not yet received" pattern the PDF itself
 * already uses for aging elsewhere (Data_Not_Received(Days)): for a
 * Submitted POD, days between delivery and upload; for a Pending one,
 * days elapsed since delivery (still waiting).
 *
 * The whole tab is scoped to delivered shipments only, matching every one
 * of the PDF's EPOD DAX measures (all filtered to Status="Delivered").
 * Counts (KPIs, transporter/aging/monthly breakdowns) dedupe by (LR No,
 * Dispatch Date) - the same fix already applied to the Performance
 * Dashboard's own Delivered_LR's Dispatch measure - so a row appearing
 * more than once for the same LR+dispatch date isn't over-counted. The
 * Shipment Level Dump stays row-level (no dedup), since it's meant to show
 * every record.
 */

const DATE_TYPE_OPTIONS = [
  { value: 'Dispatch', label: 'Dispatch Date' },
  { value: 'Invoice', label: 'Invoice Date' },
];

const TRACKING_MODE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'FTL', label: 'FTL' },
  { value: 'PTL', label: 'PTL' },
];

const POD_AGING_BUCKET_ORDER = ['On Time', '1-2 Days', '3-5 Days', '6-10 Days', 'Above 10 Days', '(Blank)'];

// Unlike dashboardShared's chartScrollWidth, this has no 20-item cap - the
// Transporter Wise Received vs Pending chart shows every transporter, so
// the scrollable width needs to grow with the real (uncapped) count.
function scrollWidthFor(count, perItem = 70, threshold = 10) {
  return count > threshold ? `${count * perItem}px` : null;
}

function isOntimeRow(row) {
  if (row.delivery_status) return row.delivery_status.toLowerCase() === 'ontime';
  return Number(row.ontime_status) === 1;
}

function getBreachBucket(row) {
  if (!isDeliveredRow(row)) return null;
  const ontime = isOntimeRow(row);
  const raw = row.transit_delay_days;
  const hasDays = raw !== undefined && raw !== null && raw !== '';
  const days = hasDays ? Number(raw) : null;
  if (ontime && (days === null || days === 0)) return 'On Time';
  if (!hasDays || isNaN(days)) return '(Blank)';
  if (!ontime && days === 0) return '0-1 Days';
  if (days > 0 && days <= 2) return '1-2 Days';
  if (days > 2 && days <= 5) return '3-5 Days';
  if (days > 5 && days <= 10) return '6-10 Days';
  if (days > 10) return 'Above 10 Days';
  return '(Blank)';
}

function dispatchDateOf(row, dateType) {
  const value = dateType === 'Invoice' ? row.invoice_time : (row.dispatch_date || row.invoice_time);
  return value ? moment(value) : null;
}

function monthKeyOf(m) {
  return m && m.isValid() ? m.format('MMM YYYY') : null;
}

function monthRange(start, end) {
  const months = [];
  let cursor = moment(start).startOf('month');
  const last = moment(end).startOf('month');
  while (cursor.isSameOrBefore(last)) {
    months.push(cursor.format('MMM YYYY'));
    cursor = cursor.add(1, 'month');
  }
  return months;
}

// Matches the Performance Dashboard's own Delivered_LR's Dispatch fix:
// COUNTROWS(SUMMARIZE(FILTER(...Delivered), LR No, Dispatch Date)).
function dedupeByLrDispatchDate(rows) {
  const seen = new Set();
  const result = [];
  rows.forEach((row) => {
    const key = `${row.lr_no}||${row.dispatch_date}`;
    if (!seen.has(key)) { seen.add(key); result.push(row); }
  });
  return result;
}

// pod_status is "Submitted" or blank on this feed - there is no stored
// "Pending" value. Blank + delivered = Pending.
function podStatusOf(row) {
  return row.pod_status && String(row.pod_status).trim() ? 'Submitted' : 'Pending';
}

// Derived from delivery_datetime + pod_upload_date (see file header).
function podDelayDaysOf(row) {
  const deliveredAt = row.delivery_datetime ? moment(row.delivery_datetime) : null;
  if (!deliveredAt || !deliveredAt.isValid()) return null;
  if (podStatusOf(row) === 'Submitted') {
    const uploadedAt = row.pod_upload_date ? moment(row.pod_upload_date) : null;
    if (!uploadedAt || !uploadedAt.isValid()) return null;
    return Math.max(0, round1(uploadedAt.diff(deliveredAt, 'days', true)));
  }
  return Math.max(0, round1(moment().diff(deliveredAt, 'days', true)));
}

// Matches the PDF's POD Pending Aging SWITCH exactly.
function podAgingBucketOf(row) {
  const days = podDelayDaysOf(row);
  if (days === null) return '(Blank)';
  if (podStatusOf(row) === 'Submitted' && days === 0) return 'On Time';
  if (days > 0 && days <= 2) return '1-2 Days';
  if (days > 2 && days <= 5) return '3-5 Days';
  if (days > 5 && days <= 10) return '6-10 Days';
  if (days > 10) return 'Above 10 Days';
  return '(Blank)';
}

// Transporter Type = IF(Transporter Category = "API", "Organized", "Unorganized").
function transporterTypeOf(row) {
  return (row.tpt_category || '').toLowerCase() === 'api' ? 'Organized' : 'Unorganized';
}

export default class PodDashboardModern extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      error: null,
      lastRefreshed: null,

      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        trackingMode: TRACKING_MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        lateDeliveryRemarks: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
        podStatus: [],
      },

      filterOptions: {
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        lateDeliveryRemarks: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
        podStatus: [{ value: 'Submitted', label: 'Submitted' }, { value: 'Pending', label: 'Pending' }],
      },

      rawRows: [],
      filteredRows: [],
      derived: null,
    };

    this.shipmentDumpColumnDefs = this.buildShipmentDumpColumnDefs();
  }

  componentDidMount() {
    this.loadLateDeliveryRemarkOptions();
    this.loadData();
  }

  loadLateDeliveryRemarkOptions() {
    redirectURL.post('/consignments/reasonForDelayData', {})
      .then((resp) => {
        const remarks = (resp.data && resp.data[0] && resp.data[0].master_data) || [];
        this.setState((prev) => ({
          filterOptions: { ...prev.filterOptions, lateDeliveryRemarks: remarks.map((r) => ({ value: r, label: r })) },
        }));
      })
      .catch(() => {});
  }

  buildPayload() {
    const f = this.state.filters;
    return {
      startDate: moment(f.startDate).format('YYYY-MM-DD'),
      endDate: moment(f.endDate).format('YYYY-MM-DD'),
      // Confirmed from the actual Flask source: getDataForPowerBiDashBoard
      // takes the short "dispatch"/"invoice" tokens, not the literal field
      // names - sending the literal names silently failed the backend's own
      // token check and left date_filter as None every time.
      date_filter: f.dateType.value === 'Invoice' ? 'invoice' : 'dispatch',
    };
  }

  loadData = () => {
    this.setState({ loading: true, error: null });
    redirectURL.post('consignments/getDataForPowerBiDashBoard', this.buildPayload())
      .then((resp) => {
        const rows = safeArray(resp.data);
        rows.forEach((row) => {
          row.movement_type_derived = /stock transfer/i.test(row.invoice_type || '') ? 'Primary' : 'Secondary';
          row.pod_status_derived = podStatusOf(row);
          row.pod_aging_bucket = podAgingBucketOf(row);
          row.transporter_type_derived = transporterTypeOf(row);
        });
        const delivered = rows.filter(isDeliveredRow);
        const filterOptions = {
          entity: distinctValues(delivered, 'truck_seal_entity').map((v) => ({ value: v, label: v })),
          movementType: distinctValues(delivered, 'movement_type_derived').map((v) => ({ value: v, label: v })),
          sourceZone: distinctValues(delivered, 'consigner_zone').map((v) => ({ value: v, label: v })),
          sourceCity: distinctValues(delivered, 'consigner_city').map((v) => ({ value: v, label: v })),
          destinationZone: distinctValues(delivered, 'consignee_zone').map((v) => ({ value: v, label: v })),
          transporterCategory: distinctValues(delivered, 'tpt_category').map((v) => ({ value: v, label: v })),
          invoiceType: distinctValues(delivered, 'invoice_type').map((v) => ({ value: v, label: v })),
          transporter: distinctValues(delivered, 'transporter_name').map((v) => ({ value: v, label: v })),
        };
        this.setState((prev) => ({
          loading: false,
          lastRefreshed: moment(),
          rawRows: rows,
          filterOptions: { ...prev.filterOptions, ...filterOptions },
        }));
        this.applyClientFilters(rows);
      })
      .catch((error) => {
        console.error('Error fetching POD dashboard data:', error);
        this.setState({ loading: false, error: 'Unable to load POD dashboard data. Please try again.' });
      });
  };

  applyClientFilters = (rowsOverride) => {
    const rows = rowsOverride || this.state.rawRows;
    const f = this.state.filters;
    const matches = (row, selection, field) => {
      if (!selection.length) return true;
      return selection.map((o) => o.value).includes(row[field]);
    };
    const filtered = rows.filter(isDeliveredRow).filter((row) => (
      matches(row, f.entity, 'truck_seal_entity')
      && matches(row, f.movementType, 'movement_type_derived')
      && matches(row, f.sourceZone, 'consigner_zone')
      && matches(row, f.sourceCity, 'consigner_city')
      && matches(row, f.destinationZone, 'consignee_zone')
      && matches(row, f.transporterCategory, 'tpt_category')
      && matches(row, f.invoiceType, 'invoice_type')
      && matches(row, f.lateDeliveryRemarks, 'reason_for_delay')
      && matches(row, f.transporter, 'transporter_name')
      && matches(row, f.podStatus, 'pod_status_derived')
      && (f.trackingMode.value === 'All' || row.tracking_type === f.trackingMode.value)
    ));
    this.setState({ filteredRows: filtered, derived: this.computeDerived(filtered) });
  };

  computeDerived(rows) {
    const { filters } = this.state;
    const deduped = dedupeByLrDispatchDate(rows);
    const totalDelivered = deduped.length;
    const receivedRows = deduped.filter((r) => r.pod_status_derived === 'Submitted');
    const pendingRows = deduped.filter((r) => r.pod_status_derived === 'Pending');
    const receivedCount = receivedRows.length;
    const pendingCount = pendingRows.length;
    const receivedPct = totalDelivered ? round1((receivedCount / totalDelivered) * 100) : 0;
    const pendingPct = totalDelivered ? round1((pendingCount / totalDelivered) * 100) : 0;

    // 2. Transporter Wise Received vs Pending
    const transporterGroups = groupBy(deduped, (r) => r.transporter_name || 'Unspecified');
    const transporterReceivedVsPending = Array.from(transporterGroups.entries())
      .map(([name, items]) => ({
        name,
        received: items.filter((r) => r.pod_status_derived === 'Submitted').length,
        pending: items.filter((r) => r.pod_status_derived === 'Pending').length,
      }))
      .sort((a, b) => (b.received + b.pending) - (a.received + a.pending));

    // 3. Status Comparison by Aging Bucket
    const agingGroups = groupBy(deduped, (r) => r.pod_aging_bucket || '(Blank)');
    const agingBuckets = POD_AGING_BUCKET_ORDER
      .filter((name) => name !== '(Blank)' || (agingGroups.get('(Blank)') || []).length > 0)
      .map((name) => {
        const items = agingGroups.get(name) || [];
        return {
          name,
          received: items.filter((r) => r.pod_status_derived === 'Submitted').length,
          pending: items.filter((r) => r.pod_status_derived === 'Pending').length,
        };
      });

    // 4. Monthly Received vs Pending
    const months = monthRange(filters.startDate, filters.endDate);
    const monthGroups = groupBy(deduped, (r) => monthKeyOf(dispatchDateOf(r, filters.dateType.value)));
    const monthlyReceivedVsPending = {
      categories: months,
      received: months.map((m) => (monthGroups.get(m) || []).filter((r) => r.pod_status_derived === 'Submitted').length),
      pending: months.map((m) => (monthGroups.get(m) || []).filter((r) => r.pod_status_derived === 'Pending').length),
      receivedPct: months.map((m) => {
        const items = monthGroups.get(m) || [];
        return items.length ? round1((items.filter((r) => r.pod_status_derived === 'Submitted').length / items.length) * 100) : null;
      }),
    };

    return {
      totalDelivered,
      receivedCount,
      pendingCount,
      receivedPct,
      pendingPct,
      transporterReceivedVsPending,
      agingBuckets,
      monthlyReceivedVsPending,
    };
  }

  onFilterChange = (key, value) => {
    this.setState((prev) => ({ filters: { ...prev.filters, [key]: value } }));
  };

  onApply = () => { this.loadData(); };

  onReset = () => {
    this.setState({
      filters: {
        startDate: moment().subtract(30, 'days').toDate(),
        endDate: moment().toDate(),
        dateType: DATE_TYPE_OPTIONS[0],
        trackingMode: TRACKING_MODE_OPTIONS[0],
        entity: [],
        movementType: [],
        sourceZone: [],
        sourceCity: [],
        destinationZone: [],
        lateDeliveryRemarks: [],
        transporterCategory: [],
        invoiceType: [],
        transporter: [],
        podStatus: [],
      },
    }, () => this.loadData());
  };

  renderEmptyState(message, hint) {
    return (
      <div className="pdm-empty-state">
        <div className="pdm-empty-state-icon">&#9679;</div>
        <strong>{message}</strong>
        {hint && <span>{hint}</span>}
      </div>
    );
  }

  renderChartCard(title, subtitle, options, height = 320, minWidth = null) {
    return (
      <div className="pdm-card">
        {title && <p className="pdm-card-title">{title}</p>}
        {subtitle && <p className="pdm-card-subtitle">{subtitle}</p>}
        {options ? (
          <div className={minWidth ? 'pdm-chart-flex pdm-chart-scroll' : 'pdm-chart-flex'} style={{ height }}>
            <div style={{ height: '100%', minWidth: minWidth || undefined }}>
              <HighchartsReact
                highcharts={Highcharts}
                containerProps={{ style: { width: '100%', height: '100%' } }}
                options={{ ...options, chart: { ...options.chart, height } }}
                updateArgs={[true, true, true]}
              />
            </div>
          </div>
        ) : this.renderEmptyState('No data available', 'Try widening the date range or clearing a filter.')}
      </div>
    );
  }

  buildTransporterReceivedPendingOptions(items) {
    if (!items.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'column' },
      title: { text: null },
      xAxis: { ...baseAxisLine(items.map((i) => i.name)), labels: { ...baseAxisLine([]).labels, rotation: items.length > 8 ? -35 : 0 } },
      yAxis: baseValueAxis('LR Count'),
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: { series: { borderRadius: 4, borderWidth: 0, dataLabels: { enabled: true, style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } } },
      tooltip: { shared: true },
      series: [
        { name: 'Received', data: items.map((i) => i.received), color: COLORS.good },
        { name: 'Pending', data: items.map((i) => i.pending), color: COLORS.warning },
      ],
    };
  }

  buildAgingBucketOptions(buckets) {
    if (!buckets.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type: 'column' },
      title: { text: null },
      xAxis: baseAxisLine(buckets.map((b) => b.name)),
      yAxis: baseValueAxis('LR Count'),
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: { series: { borderRadius: 4, borderWidth: 0, dataLabels: { enabled: true, style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } } },
      tooltip: { shared: true },
      series: [
        { name: 'Received', data: buckets.map((b) => b.received), color: COLORS.good },
        { name: 'Pending', data: buckets.map((b) => b.pending), color: COLORS.warning },
      ],
    };
  }

  buildMonthlyReceivedPendingOptions(trend) {
    if (!trend.categories.length) return null;
    return {
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart },
      title: { text: null },
      xAxis: baseAxisLine(trend.categories),
      yAxis: [
        { title: { text: 'LR Count', style: { color: COLORS.muted, fontSize: '11px' } }, gridLineColor: COLORS.grid, labels: { style: { color: COLORS.muted, fontSize: '11px' } } },
        { title: { text: '% POD Received', style: { color: COLORS.good, fontSize: '11px' } }, gridLineColor: COLORS.grid, labels: { style: { color: COLORS.muted, fontSize: '11px' } }, opposite: true, max: 100, min: 0 },
      ],
      legend: { enabled: true, itemStyle: { color: COLORS.ink, fontSize: '12px' } },
      plotOptions: {
        column: { borderRadius: 4, borderWidth: 0, dataLabels: { enabled: true, style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } },
        spline: { lineWidth: 2, marker: { radius: 4 }, connectNulls: true, dataLabels: { enabled: true, format: '{point.y}%', style: { fontSize: '10px', color: COLORS.ink, textOutline: 'none' } } },
      },
      tooltip: { shared: true },
      series: [
        { name: 'Received', type: 'column', data: trend.received, color: COLORS.good, yAxis: 0 },
        { name: 'Pending', type: 'column', data: trend.pending, color: COLORS.warning, yAxis: 0 },
        { name: '% POD Received', type: 'spline', dashStyle: 'Dash', data: trend.receivedPct, color: COLORS.yellow, yAxis: 1, tooltip: { valueSuffix: '%' }, dataLabels: { y: -10 } },
      ],
    };
  }

  buildShipmentDumpColumnDefs() {
    return [
      { headerName: 'LR No', field: 'lr_no', width: 130, pinned: 'left' },
      { headerName: 'Invoice No', field: 'invoice_no', width: 130 },
      { headerName: 'Invoice Type', field: 'invoice_type', width: 150 },
      { headerName: 'Dispatch Date', field: 'dispatch_date', width: 150, valueGetter: (p) => (p.data.dispatch_date ? getHyphenDDMMYYYY(p.data.dispatch_date) : '') },
      { headerName: 'Source Zone', field: 'consigner_zone', width: 130 },
      { headerName: 'Destination Zone', field: 'consignee_zone', width: 140 },
      { headerName: 'Destination State', field: 'consignee_state', width: 150 },
      { headerName: 'Destination City', field: 'consignee_city', width: 140 },
      { headerName: 'Mode', field: 'tracking_type', width: 100 },
      { headerName: 'Transporter', field: 'transporter_name', width: 180 },
      { headerName: 'Transporter Type', field: 'transporter_type_derived', width: 150 },
      { headerName: 'Vehicle Type', field: 'truck_type', width: 130 },
      { headerName: 'Actual Delivery Date', field: 'delivery_datetime', width: 170, valueGetter: (p) => (p.data.delivery_datetime ? getHyphenDDMMMYYYYHHMM(p.data.delivery_datetime) : '') },
      { headerName: 'Expected ETA', field: 'expected_eta', width: 160, valueGetter: (p) => (p.data.expected_eta ? getHyphenDDMMMYYYYHHMM(p.data.expected_eta) : '') },
      { headerName: 'TAT', field: 'transit_time', width: 100 },
      { headerName: 'Performance', width: 110, valueGetter: (p) => (isOntimeRow(p.data) ? 'Ontime' : 'Delay') },
      { headerName: 'Delay Bucket', width: 130, valueGetter: (p) => getBreachBucket(p.data) || '' },
      { headerName: 'POD Status', field: 'pod_status_derived', width: 120 },
      { headerName: 'POD Aging (Days)', width: 140, valueGetter: (p) => { const d = podDelayDaysOf(p.data); return d === null ? '' : d; } },
      {
        headerName: 'Customer Promise Date',
        field: 'final_eta_dt',
        width: 180,
        valueGetter: (p) => (p.data.final_eta_dt ? getHyphenDDMMMYYYYHHMM(p.data.final_eta_dt) : ''),
      },
      { headerName: 'Late Delivery Remarks', field: 'reason_for_delay', width: 200 },
    ];
  }

  render() {
    const { state } = this;
    const f = state.filters;
    const fo = state.filterOptions;
    const d = state.derived;

    return (
      <>
        {state.loading && (
          <div className="pdm-loading-overlay"><div className="pdm-spinner" /></div>
        )}
        {state.error && <div className="pdm-error-banner">{state.error}</div>}

        <div className="pdm-filterbar">
          <div className="pdm-field">
            <label className="pdm-filter-required">From</label>
            <DatePicker className="pdm-datepicker" selected={f.startDate} onChange={(v) => this.onFilterChange('startDate', v)} dateFormat="dd/MM/yyyy" maxDate={f.endDate} />
          </div>
          <div className="pdm-field">
            <label className="pdm-filter-required">To</label>
            <DatePicker className="pdm-datepicker" selected={f.endDate} onChange={(v) => this.onFilterChange('endDate', v)} dateFormat="dd/MM/yyyy" minDate={f.startDate} maxDate={new Date()} />
          </div>
          <div className="pdm-field">
            <label>Date Basis</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.dateType} options={DATE_TYPE_OPTIONS} onChange={(v) => this.onFilterChange('dateType', v)} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Entity</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.entity} options={fo.entity} onChange={(v) => this.onFilterChange('entity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Mode</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.movementType} options={fo.movementType} onChange={(v) => this.onFilterChange('movementType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceZone} options={fo.sourceZone} onChange={(v) => this.onFilterChange('sourceZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Source City</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.sourceCity} options={fo.sourceCity} onChange={(v) => this.onFilterChange('sourceCity', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Destination Zone</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.destinationZone} options={fo.destinationZone} onChange={(v) => this.onFilterChange('destinationZone', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Late Delivery Remarks</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.lateDeliveryRemarks} options={fo.lateDeliveryRemarks} onChange={(v) => this.onFilterChange('lateDeliveryRemarks', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter Category</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporterCategory} options={fo.transporterCategory} onChange={(v) => this.onFilterChange('transporterCategory', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Invoice Type</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.invoiceType} options={fo.invoiceType} onChange={(v) => this.onFilterChange('invoiceType', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>Transporter</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.transporter} options={fo.transporter} onChange={(v) => this.onFilterChange('transporter', v || [])} />
          </div>
          <div className="pdm-field pdm-field-grow">
            <label>POD Status</label>
            <Select isMulti isClearable styles={MULTI_SELECT_STYLES} value={f.podStatus} options={fo.podStatus} onChange={(v) => this.onFilterChange('podStatus', v || [])} />
          </div>
          <div className="pdm-field">
            <label>Tracking Mode (FTL/PTL)</label>
            <Select styles={MULTI_SELECT_STYLES} value={f.trackingMode} options={TRACKING_MODE_OPTIONS} onChange={(v) => this.onFilterChange('trackingMode', v)} />
          </div>
          <div className="pdm-filter-toolbar">
            <button className="pdm-btn pdm-btn-ghost" onClick={this.onReset}>Reset</button>
            <button className="pdm-btn pdm-btn-primary" onClick={this.onApply}>Apply Filters</button>
          </div>
        </div>

        {!d ? (
          <div className="pdm-card">{this.renderEmptyState('Loading dashboard...', null)}</div>
        ) : d.totalDelivered === 0 ? (
          <div className="pdm-card">{this.renderEmptyState('No delivered shipments in range', 'Widen the date range or clear a filter to see results.')}</div>
        ) : (
          <>
            <div className="pdm-kpi-grid">
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Delivered LR's</div>
                <div className="pdm-kpi-value">{d.totalDelivered}</div>
                <div className="pdm-kpi-sub">Current filters</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Received</div>
                <div className="pdm-kpi-value">{d.receivedCount}</div>
                <div className="pdm-kpi-sub">{d.receivedPct}% of delivered</div>
              </div>
              <div className="pdm-kpi-card">
                <div className="pdm-kpi-label">Pending</div>
                <div className="pdm-kpi-value">{d.pendingCount}</div>
                <div className="pdm-kpi-sub">{d.pendingPct}% of delivered</div>
              </div>
            </div>

            <div className="pdm-section">
              {this.renderChartCard(
                'Transporter Wise Received vs Pending',
                'All transporters - scroll to see more.',
                this.buildTransporterReceivedPendingOptions(d.transporterReceivedVsPending),
                380,
                scrollWidthFor(d.transporterReceivedVsPending.length),
              )}
            </div>

            <div className="pdm-section">
              {this.renderChartCard(
                'Status Comparison by Aging Bucket',
                'Received vs Pending by POD aging bucket.',
                this.buildAgingBucketOptions(d.agingBuckets),
                340,
              )}
            </div>

            <div className="pdm-section">
              {this.renderChartCard(
                'Monthly Received vs Pending',
                'Received/Pending counts by month, with % POD Received overlaid.',
                this.buildMonthlyReceivedPendingOptions(d.monthlyReceivedVsPending),
                380,
              )}
            </div>

            <div className="pdm-section">
              <div className="pdm-section-head">
                <div>
                  <h2>Shipment Level Dump</h2>
                  <p>Full LR-wise detail for delivered shipments under the selected filters.</p>
                </div>
              </div>
              <GridCard
                title="Shipment Level Dump"
                loading={state.loading}
                onRefresh={this.loadData}
                height={600}
                records={state.filteredRows}
                columnDefs={this.shipmentDumpColumnDefs}
              />
            </div>
          </>
        )}
      </>
    );
  }
}
